// Call library (user 7 Oct 2026): researchers and admins add calls / whistles of the tonal sections (humpback WC,
// baleen PAM, whistles DC, manatees MC); an admin approves them. Each entry keeps a spectrogram image, a waveform
// image and the sound of the call, plus its code, species, drawn lines / boxes and measurements.
// Visibility: approved entries from recordings shared with the community; the author sees their own; admins all.
import { amax } from './safeMath';
import { resample } from './clickData';

export const LIBRARY_SECTIONS = ['humpback', 'baleen', 'delphinid_tonal', 'manatee'];

// --- WAV (PCM 16 / 24 / 32-bit int, 32-bit float) ---------------------------------------------------------------
export function parseWav(buf) {
  const v = new DataView(buf); let p = 12; let fmt = null; let data = null;
  while (p + 8 <= v.byteLength) {
    const id = String.fromCharCode(v.getUint8(p), v.getUint8(p + 1), v.getUint8(p + 2), v.getUint8(p + 3)); const n = v.getUint32(p + 4, true);
    if (id === 'fmt ') fmt = { tag: v.getUint16(p + 8, true), ch: v.getUint16(p + 10, true), fs: v.getUint32(p + 12, true), bits: v.getUint16(p + 22, true) };
    if (id === 'data') { data = [p + 8, Math.min(n, v.byteLength - p - 8)]; break; }
    p += 8 + n + (n % 2);
  }
  if (!fmt || !data) throw new Error('not a WAV file');
  const bps = fmt.bits / 8; const frames = Math.floor(data[1] / (bps * fmt.ch)); const x = new Float32Array(frames);
  for (let i = 0; i < frames; i += 1) {
    const o = data[0] + i * bps * fmt.ch;
    if (fmt.tag === 3) x[i] = v.getFloat32(o, true);
    else if (fmt.bits === 16) x[i] = v.getInt16(o, true) / 32768;
    else if (fmt.bits === 24) x[i] = (v.getUint8(o) | (v.getUint8(o + 1) << 8) | (v.getInt8(o + 2) << 16)) / 8388608;
    else if (fmt.bits === 32) x[i] = v.getInt32(o, true) / 2147483648;
  }
  return { x, fs: fmt.fs };
}

// Browsers do not play WAV files below 3 kHz (checked in Chromium 8 Oct 2026: 2000 Hz fails, 3000 Hz plays), and
// blue / fin whale calls (15-25 Hz) are below hearing. So the library's sound is written at >= 8 kHz, and calls whose
// top frequency is under 40 Hz are played 10x faster (pitch x10, duration / 10) — the entry says so.
export const MIN_PLAY_FS = 8000;
export const speedFor = (call) => (call && call.f1 < 40 ? 10 : 1);
export function playable(x, fs, speed = 1) {
  const f = fs * speed;
  if (f >= MIN_PLAY_FS) return { x, fs: Math.round(f) };
  const m = Math.round((x.length * MIN_PLAY_FS) / f);
  return { x: resample(Array.from(x), m), fs: MIN_PLAY_FS };
}

export function wavBlob(x0, fs0, speed = 1) {
  const { x, fs } = playable(x0, fs0, speed);
  const n = x.length; const buf = new ArrayBuffer(44 + 2 * n); const v = new DataView(buf);
  const s = (o, t) => [...t].forEach((ch, i) => v.setUint8(o + i, ch.charCodeAt(0)));
  s(0, 'RIFF'); v.setUint32(4, 36 + 2 * n, true); s(8, 'WAVE'); s(12, 'fmt '); v.setUint32(16, 16, true); v.setUint16(20, 1, true);
  v.setUint16(22, 1, true); v.setUint32(24, fs, true); v.setUint32(28, fs * 2, true); v.setUint16(32, 2, true); v.setUint16(34, 16, true);
  s(36, 'data'); v.setUint32(40, 2 * n, true);
  const peak = Math.max(1e-9, amax(Array.from(x, Math.abs)));
  for (let i = 0; i < n; i += 1) v.setInt16(44 + 2 * i, Math.max(-32767, Math.min(32767, Math.round((x[i] / peak) * 0.9 * 32767))), true);
  return new Blob([buf], { type: 'audio/wav' });
}

const toBlob = (cv) => new Promise((res) => cv.toBlob((b) => res(b), 'image/png'));
const loadImg = (src) => new Promise((res, rej) => { const im = new Image(); im.crossOrigin = 'anonymous'; im.onload = () => res(im); im.onerror = () => rej(new Error('image did not load')); im.src = src; });

/** the call's window: its box + 10 % margin (at least 0.1 s), inside the frame */
export function callWindow(c, call) {
  const pad = Math.max(0.1, 0.1 * (call.t1 - call.t0));
  return [Math.max(c.x0, call.t0 - pad), Math.min(c.x1, call.t1 + pad)];
}

/** spectrogram image of the window (cropped from the review picture), waveform image and sound (from c.audio) */
export async function makeAssets(c, call, resolve = (u) => u, sig = null) {
  const [a, b] = callWindow(c, call);
  // spectrogram: crop the frame picture (x0..x1, 0..fmax)
  const im = await loadImg(resolve(c.img));
  const sx = ((a - c.x0) / (c.x1 - c.x0)) * im.naturalWidth; const sw = ((b - a) / (c.x1 - c.x0)) * im.naturalWidth;
  const cs = document.createElement('canvas'); cs.width = Math.max(50, Math.round(sw)); cs.height = im.naturalHeight;
  cs.getContext('2d').drawImage(im, sx, 0, sw, im.naturalHeight, 0, 0, cs.width, cs.height);
  const spec = await toBlob(cs);
  // sound: with the call's context sound loaded (engine 0.4.12, sig) the library keeps the seconds around the call too,
  // so library calls zoom out and play like calls in the review (13 Oct 2026; at most 12 s); else the frame's sound
  let audio = null; let wave = null; let fs = null; let seconds = b - a; let speed = 1; let win = [a, b];
  let src = null;
  if (sig && sig.x && sig.x.length) {
    const half = Math.max((b - a) / 2, 6); const mid = (call.t0 + call.t1) / 2;
    src = { x: sig.x, fs: sig.fs, t0: sig.t0, a: Math.max(sig.t0, mid - half), b: Math.min(sig.t1, mid + half) };
  } else if (c.audio && c.audio.url) {
    const r = await fetch(resolve(c.audio.url)); if (!r.ok) throw new Error(`sound ${r.status}`);
    const { x, fs: f } = parseWav(await r.arrayBuffer());
    src = { x, fs: f, t0: c.audio.t0, a, b };
  }
  if (src) {
    fs = src.fs; speed = speedFor(call);
    const cut = (p, q) => src.x.slice(Math.max(0, Math.round((p - src.t0) * fs)), Math.min(src.x.length, Math.round((q - src.t0) * fs)));
    const all = cut(src.a, src.b); win = [Math.max(src.a, src.t0), Math.max(src.a, src.t0) + all.length / fs]; seconds = all.length / fs;
    audio = wavBlob(all, fs, speed);
    const seg = cut(a, b);                               // the waveform picture shows the call's window only
    // waveform picture (min / max per column)
    const W = 900; const H = 220; const cv = document.createElement('canvas'); cv.width = W; cv.height = H; const g = cv.getContext('2d');
    g.fillStyle = '#ffffff'; g.fillRect(0, 0, W, H); g.strokeStyle = '#d0d0d0'; g.beginPath(); g.moveTo(0, H / 2); g.lineTo(W, H / 2); g.stroke();
    const peak = Math.max(1e-9, amax(Array.from(seg, Math.abs))); g.fillStyle = '#1f6fd1';
    const per = seg.length / W;
    for (let k = 0; k < W; k += 1) {
      let lo = 0; let hi = 0; for (let j = Math.floor(k * per); j < Math.min(seg.length, Math.floor((k + 1) * per) + 1); j += 1) { if (seg[j] < lo) lo = seg[j]; if (seg[j] > hi) hi = seg[j]; }
      const y0 = H / 2 - (hi / peak) * (H / 2 - 6); const y1 = H / 2 - (lo / peak) * (H / 2 - 6); g.fillRect(k, y0, 1, Math.max(1, y1 - y0));
    }
    wave = await toBlob(cv);
  }
  return { spec, wave, audio, window: win, sampleRate: fs, seconds, speed };
}

/** the library record (without the files) for a call under review */
// sharing (user 13 Oct 2026): 'private' = only in the author's own library (no approval); 'community' = for the
// platform library after an admin approves it; 'both' = in the author's library at once AND sent for approval.
export const SHARE = ['private', 'community', 'both'];
export const inMyLibrary = (e) => e.share === 'private' || e.share === 'both';
export const inCommunity = (e) => e.status === 'approved' && e.public === true && e.share !== 'private';
export function entryFor(c, state, section, meta, review, share = 'community') {
  const call = state.call;
  return {
    section: section.key, codeSystem: section.codeSystem, code: state.label.code || '', broad: state.label.broad || '',
    speciesId: state.label.speciesId || c.speciesId || null, speciesLabel: state.label.speciesLabel || c.species || null,
    itemId: meta.itemId || c._meta?.itemId || null, stationId: meta.stationId || c._meta?.stationId || null,
    // community: the call (not the recording) is shared — from a private recording the file name is left out
    share, public: share !== 'private', recordedFile: share === 'private' || !c._item || c._item.public === true ? c.file || null : null,
    fromPrivateRecording: !!(c._item && c._item.public !== true),
    call: { t0: call.t0, t1: call.t1, f0: call.f0, f1: call.f1 },
    frame: { x0: c.x0, x1: c.x1, fmax: c.fmax, sampleRate: c.sampleRate || null },
    lines: review ? review.lines : [], boxes: review ? review.boxes : [],
    measurements: review ? review.measurements : {},
  };
}

// --- starter entries (user, 8 Oct 2026): humpback = Whale Chat example calls (code by the engine, sound + pictures);
// baleen = the Code document's reference pictures only. An admin loads them once; they are replaced by recorded
// calls later ("Approve and replace the starter example"). Files: public/library-starter (validation/library_starter.py).
const PUB = process.env.PUBLIC_URL || '';
let starter = null;
export function loadStarter() {
  if (!starter) starter = fetch(`${PUB}/library-starter/manifest.json`).then((r) => { if (!r.ok) throw new Error(`starter list ${r.status}`); return r.json(); })
    .then((d) => d.entries).catch((e) => { starter = null; throw e; });
  return starter;
}
export const starterUrl = (path) => (path ? `${PUB}/${path}` : null);
/** the record (without files) and the files as blobs, ready for library.add; get(path) -> Blob (default: public files) */
export async function starterAssets(e, get = null) {
  const fetchPub = async (path) => { const r = await fetch(starterUrl(path)); if (!r.ok) throw new Error(`${path} ${r.status}`); return r.blob(); };
  const one = async (path) => (path ? (get || fetchPub)(path) : null);
  const [spec, wave, audio] = await Promise.all([one(e.files.spec), one(e.files.wave), one(e.files.audio)]);
  const { files, addedAt, sound, ...record } = e;
  return { record: { ...record, starter: true, itemId: null, stationId: null },
    assets: { spec, wave, audio, sampleRate: sound ? sound.sampleRate : null, seconds: sound ? sound.seconds : null, window: sound ? sound.window : null,
      speed: sound && sound.speed ? sound.speed : 1 } };
}

/** a folder the admin picked (<input webkitdirectory>): its manifest.json entries and a getter for its files.
 *  Used for the admin-only reference calls, which are never part of the public web site. */
export async function readFolder(fileList) {
  // the picked folder may be the reference folder itself or any folder above it: the shallowest manifest.json is the root
  const all = [...fileList].map((f) => ({ f, p: f.webkitRelativePath || f.name }));
  const man = all.filter((x) => x.p.split('/').pop() === 'manifest.json').sort((a, b) => a.p.split('/').length - b.p.split('/').length)[0];
  if (!man) throw new Error('no manifest.json in this folder — pick the reference folder itself (library-admin-refs for baleen whales, library-admin-refs-manatee for manatees)');
  const root = man.p.slice(0, man.p.length - 'manifest.json'.length);
  const files = new Map();
  all.forEach((x) => { if (x.p.startsWith(root)) files.set(x.p.slice(root.length), x.f); });
  const m = man.f;
  const entries = JSON.parse(await m.text()).entries || [];
  const get = async (path) => { const f = files.get(path); if (!f) throw new Error(`${path} is missing`); return f; };
  return { entries, get };
}
